import type { CustomBetSelectionRequest } from '@/api/custom-bets';
import {
  BET_BUILDER_MAX_CUSTOM_LEGS,
  BET_BUILDER_MAX_SELECTIONS,
} from './bet-builder';
import {
  applyTicketTypeRules,
  countBets,
  createEmptyTicket,
  DEFAULT_BETSLIP_STAKE,
  DEFAULT_MAX_COMBI,
  type BetslipBet,
  type BetslipCustomSelection,
  type BetslipItem,
  type BetslipSelectionDraft,
  type BetslipSystemLimits,
  type BetslipTicket,
  type BetslipToggleOptions,
} from './betslip';

export type CustomBetToggleReason =
  | 'straight_mixed'
  | 'max_custom_legs'
  | 'max_selections'
  | 'max_combi'
  | 'invalid';

export type CustomBetToggleResult =
  | { ok: true; ticket: BetslipTicket }
  | { ok: false; reason: CustomBetToggleReason };

export interface CustomBetSelectionDraft extends BetslipSelectionDraft {
  /** Display market name for nested legs. */
  marketName?: string;
}

function eventIdsEqual(a: string | number, b: string | number): boolean {
  return String(a) === String(b);
}

function cloneTicket(ticket: BetslipTicket): BetslipTicket {
  return structuredClone(ticket);
}

function nowTimestamp(): string {
  const d = new Date();
  const pad = (n: number, len = 2) => String(n).padStart(len, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
}

/** Stable key for a nested Bet Builder selection (legacy `SelectionKey`). */
export function customSelectionKey(
  selection: Pick<BetslipCustomSelection, 'MarketId' | 'Outcome' | 'OutcomeId' | 'Specifier'>,
): string {
  const outcome = selection.Outcome ?? selection.OutcomeId ?? '';
  return `${selection.MarketId ?? ''}|${outcome}|${selection.Specifier ?? ''}`;
}

export function countCustomLegs(ticket: BetslipTicket): number {
  let count = 0;
  for (const item of ticket.Items) {
    for (const bet of item.Bets) {
      if (bet.Custom) count += 1;
    }
  }
  return count;
}

export function findCustomItemIndex(
  ticket: BetslipTicket,
  eventId: string | number,
): number {
  return ticket.Items.findIndex(
    (item) =>
      eventIdsEqual(item.Event.EventId, eventId) &&
      item.Bets.some((bet) => bet.Custom === true),
  );
}

export function hasCustomOnEvent(ticket: BetslipTicket, eventId: string | number): boolean {
  return findCustomItemIndex(ticket, eventId) >= 0;
}

export function hasStraightOnEvent(ticket: BetslipTicket, eventId: string | number): boolean {
  const item = ticket.Items.find((entry) => eventIdsEqual(entry.Event.EventId, eventId));
  if (!item) return false;
  return item.Bets.some((bet) => bet.Custom !== true);
}

function draftToCustomSelection(draft: CustomBetSelectionDraft): BetslipCustomSelection {
  return {
    EventId: draft.eventId,
    MarketId: draft.marketId,
    Market: draft.marketName ?? draft.market,
    Outcome: draft.outcome,
    OutcomeId: draft.outcomeId ?? draft.outcome,
    OutcomeName: draft.outcomeName ?? draft.outcome,
    Specifier: draft.specifier ?? null,
    Odd: draft.odd,
    FeedType: draft.feedType,
  };
}

function buildCustomEventBlock(draft: CustomBetSelectionDraft): BetslipItem['Event'] {
  return {
    EventId: draft.eventId,
    Home: draft.home,
    Away: draft.away,
    HomeScore: draft.homeScore ?? '',
    AwayScore: draft.awayScore ?? '',
    Date: draft.date,
    Live: draft.live ?? false,
    Banked: false,
    Category: draft.category,
    Tournament: draft.tournament,
    TournamentId: draft.tournamentId,
    Outright: false,
    Sport: draft.sport,
    SportId: draft.sportId,
  };
}

function emptyCustomBet(feedType?: number): BetslipBet {
  return {
    MarketId: null,
    Market: null,
    Specifier: null,
    Outcome: null,
    OutcomeId: null,
    OutcomeName: null,
    Odd: null,
    Timestamp: nowTimestamp(),
    FeedType: feedType,
    Outright: false,
    Custom: true,
    Selections: [],
  };
}

/**
 * Toggle a Bet Builder selection onto/off the ticket (legacy `BetBuilderSelect` core).
 * Clears package `Odd` until pricing refreshes.
 */
export function toggleCustomSelection(
  ticket: BetslipTicket,
  draft: CustomBetSelectionDraft,
  options: BetslipToggleOptions = {},
): CustomBetToggleResult {
  if (!draft.marketId || !draft.outcome) {
    return { ok: false, reason: 'invalid' };
  }

  const maxCombi = options.maxCombi ?? DEFAULT_MAX_COMBI;
  const next =
    ticket.Items.length === 0
      ? createEmptyTicket(ticket.Stake || DEFAULT_BETSLIP_STAKE)
      : cloneTicket(ticket);

  if (hasStraightOnEvent(next, draft.eventId)) {
    return { ok: false, reason: 'straight_mixed' };
  }

  const selection = draftToCustomSelection(draft);
  const key = customSelectionKey(selection);
  const itemIndex = findCustomItemIndex(next, draft.eventId);

  if (itemIndex < 0) {
    if (countCustomLegs(next) >= BET_BUILDER_MAX_CUSTOM_LEGS) {
      return { ok: false, reason: 'max_custom_legs' };
    }
    if (countBets(next) >= maxCombi) {
      return { ok: false, reason: 'max_combi' };
    }
    const customBet = emptyCustomBet(draft.feedType);
    customBet.Selections = [selection];
    next.Items.push({
      Event: buildCustomEventBlock(draft),
      Bets: [customBet],
    });
  } else {
    const item = next.Items[itemIndex]!;
    const bet = item.Bets.find((entry) => entry.Custom === true) ?? item.Bets[0];
    if (!bet) return { ok: false, reason: 'invalid' };
    const selections = bet.Selections ?? (bet.Selections = []);
    const existing = selections.findIndex((sel) => customSelectionKey(sel) === key);
    if (existing >= 0) {
      selections.splice(existing, 1);
      if (selections.length === 0) {
        next.Items.splice(itemIndex, 1);
      } else {
        bet.Odd = null;
        bet.Timestamp = nowTimestamp();
      }
    } else {
      if (selections.length >= BET_BUILDER_MAX_SELECTIONS) {
        return { ok: false, reason: 'max_selections' };
      }
      selections.push(selection);
      bet.Odd = null;
      bet.Timestamp = nowTimestamp();
    }
  }

  next.Changed = false;
  return {
    ok: true,
    ticket: applyTicketTypeRules(next, {
      minSystem: options.minSystem,
      maxSystem: options.maxSystem,
    }),
  };
}

/** Remove one nested selection from a Custom package; drops the item when empty. */
export function removeCustomSelection(
  ticket: BetslipTicket,
  eventId: string | number,
  selectionKey: string,
  limits?: Partial<BetslipSystemLimits>,
): BetslipTicket {
  const next = cloneTicket(ticket);
  const itemIndex = findCustomItemIndex(next, eventId);
  if (itemIndex < 0) return ticket;

  const item = next.Items[itemIndex]!;
  const bet = item.Bets.find((entry) => entry.Custom === true);
  if (!bet?.Selections) return ticket;

  const idx = bet.Selections.findIndex((sel) => customSelectionKey(sel) === selectionKey);
  if (idx < 0) return ticket;

  bet.Selections.splice(idx, 1);
  if (bet.Selections.length === 0) {
    next.Items.splice(itemIndex, 1);
  } else {
    bet.Odd = null;
    bet.Timestamp = nowTimestamp();
  }

  return applyTicketTypeRules(next, limits);
}

/** Apply combined Custom odd from calculate-filter (or clear when null). */
export function applyCustomBetOdd(
  ticket: BetslipTicket,
  eventId: string | number,
  odd: number | null,
  limits?: Partial<BetslipSystemLimits>,
): BetslipTicket {
  const next = cloneTicket(ticket);
  const itemIndex = findCustomItemIndex(next, eventId);
  if (itemIndex < 0) return ticket;

  const bet = next.Items[itemIndex]!.Bets.find((entry) => entry.Custom === true);
  if (!bet) return ticket;

  if (odd == null || !Number.isFinite(odd) || odd <= 0) {
    bet.Odd = null;
  } else {
    bet.Odd = Math.round(odd * 100) / 100;
  }
  return applyTicketTypeRules(next, limits);
}

/**
 * Undo last selection when calculate returns odds 0 (legacy `CustomBetCannotCombine`).
 * Returns the updated ticket and whether a selection was removed.
 */
export function undoLastCustomSelection(
  ticket: BetslipTicket,
  eventId: string | number,
  limits?: Partial<BetslipSystemLimits>,
): { ticket: BetslipTicket; removed: boolean } {
  const next = cloneTicket(ticket);
  const itemIndex = findCustomItemIndex(next, eventId);
  if (itemIndex < 0) return { ticket, removed: false };

  const bet = next.Items[itemIndex]!.Bets.find((entry) => entry.Custom === true);
  if (!bet?.Selections?.length) return { ticket, removed: false };

  bet.Selections.pop();
  if (bet.Selections.length === 0) {
    next.Items.splice(itemIndex, 1);
  } else {
    bet.Odd = null;
  }

  return {
    ticket: applyTicketTypeRules(next, limits),
    removed: true,
  };
}

/** Wire payload for CBAPI calculate-filter. */
export function buildCalculateRequestSelections(
  selections: BetslipCustomSelection[],
): CustomBetSelectionRequest[] {
  return selections.map((sel) => ({
    EventId: Number(sel.EventId),
    MarketId: String(sel.MarketId ?? ''),
    OutcomeId: String(sel.OutcomeId ?? sel.Outcome ?? ''),
    Specifier: sel.Specifier ?? null,
    Odd: typeof sel.Odd === 'number' && Number.isFinite(sel.Odd) ? sel.Odd : null,
  }));
}

/** True when a Bet Builder outcome is selected on the slip for this event. */
export function isCustomSelectionInTicket(
  ticket: BetslipTicket,
  eventId: string | number,
  selection: Pick<BetslipCustomSelection, 'MarketId' | 'Outcome' | 'OutcomeId' | 'Specifier'>,
): boolean {
  const itemIndex = findCustomItemIndex(ticket, eventId);
  if (itemIndex < 0) return false;
  const bet = ticket.Items[itemIndex]!.Bets.find((entry) => entry.Custom === true);
  const key = customSelectionKey(selection);
  return (bet?.Selections ?? []).some((sel) => customSelectionKey(sel) === key);
}
